/**
 * The ONE map from the server's attempt error codes to what the user is told.
 *
 * The API puts the stable code at `error.code` of its envelope
 * (backend ApiResponse.error):
 *
 *   { success: false, message, error: { code, details } }
 *
 * and RTK Query hands that body over as `error.data`. Finalize (Record &
 * Review, canonical coaching), turns, hints and the live connection all refuse
 * with these codes; every surface words them from here, so the same refusal
 * reads the same everywhere and nothing is shown as a success or a score.
 */

import { apiErrorCode, apiErrorDetails } from './errorHelpers';

export interface AttemptErrorCopy {
  message: string;
  /** Retrying the same request can succeed (offer "Try again"). */
  retryable: boolean;
}

export const ATTEMPT_ERROR_COPY: Record<string, AttemptErrorCopy> = {
  NOTHING_TO_EVALUATE: {
    message: 'There was nothing to evaluate — no words were captured — so nothing was scored or recorded. Record again.',
    retryable: false,
  },
  NO_SPEECH_DETECTED: {
    message: 'No speech was detected in your recording, so nothing was scored or recorded. Record again.',
    retryable: false,
  },
  TRANSCRIPTION_UNAVAILABLE: {
    message: 'Your recording could not be transcribed right now, so nothing was scored or recorded. Try again.',
    retryable: true,
  },
  FINALIZE_PROVIDER_TIMEOUT: {
    message: 'Scoring took too long and was stopped; nothing was recorded. Try again.',
    retryable: true,
  },
  FINALIZE_ATTEMPTS_EXHAUSTED: {
    message: 'This attempt could not be finalized after several tries, so it will not be tried again. Start a new attempt.',
    retryable: false,
  },
  FINALIZATION_IN_PROGRESS: {
    message: 'This attempt is already being finalized. Try again in a moment.',
    retryable: true,
  },
  SESSION_ALREADY_FINALIZED: {
    message: 'This attempt was already finalized. Its stored result is shown — nothing was scored again.',
    retryable: false,
  },
  SESSION_DISCARDED: {
    message: 'This attempt was discarded, so it cannot be finalized or scored. Start a new attempt.',
    retryable: false,
  },
  USAGE_AUTHORITY_UNAVAILABLE: {
    message: 'Usage could not be checked right now, so nothing was done and nothing was counted. Try again in a moment.',
    retryable: true,
  },
  CENTRAL_UNAVAILABLE: {
    message: "We couldn't confirm your access just now, so nothing was done. Try again in a moment.",
    retryable: true,
  },
  CONVERSATION_SESSION_BUSY: {
    message: 'Another request for this conversation is still running. Try again in a moment.',
    retryable: true,
  },
  REALTIME_SESSION_BUSY: {
    message: 'This conversation is already open in another tab or window. Close it there, or try again in a moment.',
    retryable: true,
  },
  // 402: refused at admission, before any provider call or reservation.
  USAGE_BLOCKED: {
    message: "You've reached your plan's usage limit for this, so nothing was done and nothing was counted. Check your plan's usage to continue.",
    retryable: false,
  },
  // 409: the session ended (or was discarded) before this request could be recorded.
  SESSION_NOT_ACTIVE: {
    message: 'This session has already ended, so nothing was recorded. Start a new session to keep practising.',
    retryable: false,
  },
  // 429: an every-plan noncommercial SAFETY cap (daily conversation starts,
  // daily voice analyses, a practice session's voice turns). The server's own
  // limit copy is preferred (it carries the number); this is the fallback.
  // Not retryable: it frees up with time, never by trying again now.
  CONVERSATION_SAFETY_LIMIT: {
    message: "You've reached a safety limit that applies on every plan, so nothing was started or counted. It frees up with time — see Usage for the limits.",
    retryable: false,
  },
  // 409 (retryable): another recording for the same practice session is still being processed.
  PRACTICE_SESSION_BUSY: {
    message: 'Your previous recording for this session is still being processed, so this one was not sent. Try again in a moment.',
    retryable: true,
  },
};

/** The stored outcome a 409 SESSION_ALREADY_FINALIZED carries (practiceController.finishedOutcome). */
export interface StoredAttemptOutcome {
  sessionId?: string;
  transcript?: string | null;
  feedback?: { summary?: string } | null;
  resultUrl?: string | null;
}

export interface AttemptErrorView extends AttemptErrorCopy {
  code: string | null;
  /** Only for SESSION_ALREADY_FINALIZED: the result the server already stored. */
  storedOutcome?: StoredAttemptOutcome;
}

/**
 * What to tell the user about a failed attempt request. A known code reads its
 * copy above; anything else reads `fallback` (never a success, never a score).
 */
export function attemptErrorView(error: unknown, fallback: string): AttemptErrorView {
  const code = apiErrorCode(error);
  const copy = code ? ATTEMPT_ERROR_COPY[code] : undefined;
  if (!copy) return { code, message: fallback, retryable: false };
  if (code === 'SESSION_ALREADY_FINALIZED') {
    const outcome = (apiErrorDetails(error) as { outcome?: unknown } | null)?.outcome;
    return {
      code,
      ...copy,
      ...(outcome && typeof outcome === 'object' ? { storedOutcome: outcome as StoredAttemptOutcome } : {}),
    };
  }
  return { code, ...copy };
}
